import { useMemo } from 'react';
import { useGetRegionConfigQuery } from '@store/api';
import {
  REGION_QUICK_LINK_KEYS,
  REGION_QUICK_LINK_ICONS,
  STATIC_QUICK_LINKS,
  humanizeName,
} from '@config/regionLinksConfig';
import type { QuickLink as RegionQuickLink } from '@store/models/regions';
import type { QuickLink } from '@store/models/booking';

const REGION_NAME = 'NORAM';

// Only `https` is accepted: rejects `http`, `mailto:`, `javascript:`,
// relative paths, and any value that `new URL()` cannot parse.
function isSafeQuickLinkUrl(value: string): boolean {
  try {
    return new URL(value).protocol === 'https:';
  } catch {
    return false;
  }
}

function resolveLabelKey(name: string): string {
  return REGION_QUICK_LINK_KEYS[name] ?? humanizeName(name);
}

/**
 * Spec 109-fix-help-quicklinks: projects the Region API `employeeQuickLinks`
 * into the rendering shape consumed by the Help-panel `QuickLinksPanel`, then
 * appends the in-app `STATIC_QUICK_LINKS` so they are always visible
 * regardless of API state.
 *
 * Always uses `employeeQuickLinks` regardless of persona (product decision
 * 2026-06-15: planners and employees share the same Help-drawer link set).
 *
 * Filters out entries with missing fields, non-`link` type, or non-https URLs.
 * Dedupes by URL (case-insensitive) across both API and static entries.
 * Returns API order followed by static order (after drops).
 */
export function useRegionQuickLinks(): QuickLink[] {
  const { data: regionData, isError } = useGetRegionConfigQuery(REGION_NAME);

  return useMemo<QuickLink[]>(() => {
    const seenUrls = new Set<string>();
    const result: QuickLink[] = [];

    const sourceList: RegionQuickLink[] | undefined =
      !isError && regionData?.data != null ? regionData.data.employeeQuickLinks : undefined;

    if (sourceList && sourceList.length > 0) {
      for (const link of sourceList) {
        if (!link?.name || !link?.value || link.type !== 'link') continue;
        if (!isSafeQuickLinkUrl(link.value)) continue;

        const dedupeKey = link.value.toLowerCase();
        if (seenUrls.has(dedupeKey)) continue;
        seenUrls.add(dedupeKey);

        result.push({
          labelKey: resolveLabelKey(link.name),
          url: link.value,
          icon: REGION_QUICK_LINK_ICONS[link.name],
        });
      }
    }

    for (const link of STATIC_QUICK_LINKS) {
      const dedupeKey = link.url.toLowerCase();
      if (seenUrls.has(dedupeKey)) continue;
      seenUrls.add(dedupeKey);
      result.push(link);
    }

    return result;
  }, [regionData, isError]);
}
